import React from 'react';
import { Button } from "@/components/ui/button";
import { Share2, MessageSquare, Instagram } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useNightModeStyles } from "@/hooks/useNightModeStyles";
import { trackEvent } from "@/lib/analytics";
import { buildTodayShareUrl } from "@/lib/urlState";

const getEmojiTooltip = (emoji, layers) => {
  const tooltips = {
    1: {
      '👕': 't-shirt'
    },
    2: {
      '👕': 't-shirt',
      '🧥': 'light jacket'
    },
    3: {
      '👕': 't-shirt',
      '🧥': 'thick jacket',
      '🧣': 'scarf'
    },
    4: {
      '👕': 't-shirt',
      '🧥': 'winter jacket',
      '🧣': 'sweater & scarf',
      '👞': 'winter boots'
    },
    5: {
      '👕': 't-shirt',
      '🧥': 'winter jacket',
      '🧣': 'sweater & scarf',
      '🧤': 'winter gloves',
      '👞': 'winter boots'
    },
    weather: {
      '☔': 'umbrella or rain jacket'
    }
  };
  return tooltips[layers]?.[emoji] || tooltips.weather?.[emoji] || '';
};

// Function to detect if the user is on a mobile device
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Regular expressions to check for mobile devices
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return mobileRegex.test(userAgent);
};

const LayerRecommendation = ({
  recommendation,
  location,
  forecastHours = 0
}) => {
  const {
    toast
  } = useToast();
  const {
    getNightModeClass
  } = useNightModeStyles();
  // Canonical share URL — recipient lands on the exact forecast, not just
  // the homepage. Falls back to bare domain when there's no city to encode.
  const baseShareUrl = location
    ? buildTodayShareUrl({ city: location, forecastHours })
    : 'https://layers.today';

  // Per-platform UTM so we can tell from GA referrers which surfaces drive
  // shares vs which sit dead. utm_medium=share keeps the bucket distinct
  // from organic / direct.
  const withUtm = (platform) => {
    const sep = baseShareUrl.includes('?') ? '&' : '?';
    return `${baseShareUrl}${sep}utm_source=${platform}&utm_medium=share&utm_campaign=layer_rec`;
  };
  const handleShare = async platform => {
    trackEvent('share', {
      method: platform,
      content_type: 'layer_recommendation',
      value: recommendation.layers
    });
    const shareUrl = withUtm(platform);
    const shareText = `You will need ${recommendation.layers} layers ${recommendation.emojis.join('')}, ${recommendation.description}. ${shareUrl}`;
    let url;
    switch (platform) {
      case 'whatsapp':
        if (isMobileDevice()) {
          url = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
        } else {
          url = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'instagram':
        url = `instagram://share?text=${encodeURIComponent(shareText)}`;
        break;
      case 'imessage':
        url = `sms:&body=${encodeURIComponent(shareText)}`;
        break;
      default:
        await navigator.clipboard.writeText(shareText);
        toast({
          description: "Copied"
        });
        return;
    }
    try {
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open share URL:', err);
      await navigator.clipboard.writeText(shareText);
      toast({
        description: "Copied to clipboard instead"
      });
    }
  };
  return <div className={`layer-rec-pill flex items-center space-x-4 mb-3 p-3 rounded-lg ${recommendation.background} relative ${getNightModeClass()} dark:bg-gray-800/50 dark:text-gray-200`}>
      {/* Component-scoped delight: first-reveal entry on the recommendation pill.
          Fade + slight lift, 420ms (first-reveal range). The big layer number
          gets its own pop after, so the eye lands on it second. */}
      <style>{`
        .layer-rec-pill {
          opacity: 1;
          transform: translateY(0);
          transition:
            opacity 420ms cubic-bezier(0.23, 1, 0.32, 1),
            transform 420ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        @starting-style {
          .layer-rec-pill {
            opacity: 0;
            transform: translateY(8px);
          }
        }
        @keyframes layer-num-pop {
          0%   { transform: scale(0.85); opacity: 0; }
          60%  { transform: scale(1.04); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        .layer-rec-pill .layer-number {
          display: inline-block;
          animation: layer-num-pop 460ms cubic-bezier(0.23, 1, 0.32, 1) 80ms both;
        }
        @media (prefers-reduced-motion: reduce) {
          .layer-rec-pill { transition-duration: 0ms; }
          .layer-rec-pill .layer-number { animation: none; }
        }
      `}</style>
      <div className="flex items-center space-x-2">
        <span className="font-light text-5xl layer-number text-gray-800 dark:text-white">{recommendation.layers}</span>
        <div className="flex space-x-1">
          <TooltipProvider delayDuration={0}>
            {recommendation.emojis.map((emoji, index) => <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <span className="cursor-help touch-manipulation text-4xl">
                    {emoji}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getEmojiTooltip(emoji, recommendation.layers)}</p>
                </TooltipContent>
              </Tooltip>)}
          </TooltipProvider>
        </div>
      </div>
      <span className="flex-1 pr-12 font-light text-2xl layer-recommendation-text text-gray-800 dark:text-white break-words">
        {recommendation.description}
      </span>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="absolute bottom-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 button-transparent">
            <Share2 className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleShare('whatsapp')}>
            <MessageSquare className="h-4 w-4 mr-2" />
            WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('imessage')}>
            <MessageSquare className="h-4 w-4 mr-2" />
            iMessage
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('instagram')}>
            <Instagram className="h-4 w-4 mr-2" />
            Instagram
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('copy')}>
            <Share2 className="h-4 w-4 mr-2" />
            Copy Link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};
export default LayerRecommendation;
